import type { EvalRunItem, EvalRunStatus } from '../types/evalTypes';

const BADGES: Record<EvalRunStatus, { label: string; title: string; className: string }> = {
  queued: { label: 'Queued', title: 'Waiting for a worker. Two runs go at a time.', className: 'bg-blue-50 text-blue-700' },
  running: { label: 'Running', title: 'The trials are running in the background.', className: 'bg-blue-100 text-blue-800 animate-pulse' },
  completed: { label: 'Completed', title: 'Every trial ran.', className: 'bg-gray-100 text-gray-700' },
  deadline: {
    label: 'Hit time limit',
    title: 'The run reached its time limit; some trials were skipped.',
    className: 'bg-amber-100 text-amber-800',
  },
  cancelled: {
    label: 'Cancelled',
    title: 'Someone cancelled the run. Only the trials done by then are here.',
    className: 'bg-red-100 text-red-800',
  },
  failed: { label: 'Failed', title: 'The run could not start or finish.', className: 'bg-red-100 text-red-800' },
};

interface RunStatusBadgeProps {
  run: Pick<EvalRunItem, 'status' | 'error' | 'cancelRequested' | 'tokenName' | 'regradeOf'>;
  /** In lists, where most runs are completed, only the other statuses stand out. */
  hideCompleted?: boolean;
}

export const RunStatusBadge = ({ run, hideCompleted = false }: RunStatusBadgeProps) => {
  const badge = BADGES[run.status];
  const cancelling = run.status === 'running' && run.cancelRequested;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {!(hideCompleted && run.status === 'completed') && (
        <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${badge.className}`} title={run.error ?? badge.title}>
          {cancelling ? 'Cancelling…' : badge.label}
        </span>
      )}
      {run.regradeOf !== undefined && (
        <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-800" title="The outputs of another run, graded again with no model calls">
          Re-grade
        </span>
      )}
      {run.tokenName !== undefined && (
        <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-800" title={`Started by Claude with the token "${run.tokenName}"`}>
          Claude
        </span>
      )}
    </span>
  );
};
